import { useState } from "react";
import { Loader2, PencilLine, Phone, XCircle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { AddressInput } from "@/components/AddressInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const field = "h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold";

/** Passenger-driven change or cancellation, inside the window the operator allows. */
export const TripChangePanel = ({ token, trip, onDone, onError }) => {
  const [mode, setMode] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    trip_date: trip.trip_date || "",
    pickup_time: trip.pickup_time || "",
    pickup: trip.pickup || null,
    dropoff: trip.dropoff || null,
  });
  const [reason, setReason] = useState("");
  const [localError, setLocalError] = useState("");
  const win = trip.change_window || {};

  if (!win.can_change) {
    return (
      <div data-testid={`trip-locked-${trip.number}`} className="rounded-lg border border-ink-600 bg-ink-900/60 p-3.5">
        <p className="text-xs text-slate-400">{win.reason}</p>
        {win.phone && (
          <a href={`tel:${win.phone}`} data-testid={`trip-call-dispatch-${trip.number}`}
            className="mt-2 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
            <Phone className="h-3 w-3" /> Call dispatch · {win.phone}
          </a>
        )}
      </div>
    );
  }

  const run = async (fn) => {
    setBusy(true);
    setLocalError("");
    try {
      await fn();
      setMode("");
      onDone?.();
    } catch (e) {
      setLocalError(errMsg(e));
      onError?.(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!mode) {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid={`trip-edit-${trip.number}`} onClick={() => setMode("edit")}
            className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
            <PencilLine className="mr-2 h-4 w-4" /> Change this ride
          </Button>
          <Button variant="outline" data-testid={`trip-cancel-${trip.number}`} onClick={() => setMode("cancel")}
            className="border-red-500/40 bg-transparent text-red-200 hover:bg-red-500/10">
            <XCircle className="mr-2 h-4 w-4" /> Cancel
          </Button>
        </div>
        <p className="text-[11px] text-slate-500">
          You can change or cancel yourself up to {win.cutoff_hours}h before pickup.
          {win.change_fee > 0 && (
            <span className="text-amber-300">
              {" "}A ${win.change_fee.toFixed(2)} change fee applies this close to pickup.
            </span>
          )}
        </p>
      </div>
    );
  }

  if (mode === "cancel") {
    return (
      <div data-testid={`trip-cancel-form-${trip.number}`} className="space-y-3 rounded-lg border border-red-500/40 bg-red-500/5 p-4">
        <p className="text-sm text-slate-200">Cancel trip #{trip.number}?</p>
        <Input data-testid={`trip-cancel-reason-${trip.number}`} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional)" className={field} />
        <div className="flex gap-2">
          <Button data-testid={`trip-cancel-confirm-${trip.number}`} disabled={busy}
            onClick={() => run(() => api.post(`/public/rider/${token}/trips/${trip.id}/cancel`, { reason }))}
            className="bg-red-500/20 text-red-100 hover:bg-red-500/30">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, cancel my ride"}
          </Button>
          <Button variant="ghost" className="text-slate-400" onClick={() => setMode("")}>Keep it</Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`trip-edit-form-${trip.number}`} className="space-y-3 rounded-lg border border-ink-600 bg-ink-900/60 p-4">
      <div className="grid grid-cols-2 gap-3">
        <Input data-testid={`trip-edit-date-${trip.number}`} type="date" value={form.trip_date}
          onChange={(e) => setForm({ ...form, trip_date: e.target.value })} className={field} />
        <Input data-testid={`trip-edit-time-${trip.number}`} type="time" value={form.pickup_time}
          onChange={(e) => setForm({ ...form, pickup_time: e.target.value })} className={field} />
      </div>
      <AddressInput label="Pickup" testId={`trip-edit-pickup-${trip.number}`} value={form.pickup}
        onChange={(p) => setForm({ ...form, pickup: p })} />
      <AddressInput label="Drop-off" testId={`trip-edit-dropoff-${trip.number}`} value={form.dropoff}
        onChange={(p) => setForm({ ...form, dropoff: p })} />
      <div className="relative z-[950] flex gap-2">
        <Button data-testid={`trip-edit-save-${trip.number}`} disabled={busy}
          onClick={() => run(() => api.put(`/public/rider/${token}/trips/${trip.id}`, form))}
          className="bg-gold text-ink-900 hover:bg-gold-light">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
        </Button>
        <Button variant="ghost" className="text-slate-400" onClick={() => setMode("")}>Never mind</Button>
      </div>
      {win.change_fee > 0 && (
        <p data-testid={`trip-edit-fee-${trip.number}`} className="rounded border border-amber-400/40 bg-amber-400/10 p-2.5 text-xs text-amber-200">
          Rescheduling within {win.change_fee_within_hours}h of pickup adds a $
          {win.change_fee.toFixed(2)} change fee to this trip.
        </p>
      )}
      {localError && (
        <p data-testid={`trip-edit-error-${trip.number}`} className="text-xs text-red-400">{localError}</p>
      )}
      <p className="text-[11px] text-slate-500">
        Dispatch is notified straight away and your price may change with the new route.
      </p>
    </div>
  );
};
